import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';

import { PROTECTED_API_URL } from '@lib/core/tokens/api.tokens';

import { CoverageCategory, CoverageOption } from '../models/package-configurator/coverage-option.model';
import {
  CoverageOptionResponse,
  CoverageResponse,
  PricingResponse,
  fromCoverageResponse,
  fromOptionResponse,
  fromPricingResponse,
  toCategoryPayload,
  toPricingPayload,
} from '../models/package-configurator/coverage-response.mapper';
import { buildDefaultGeneralInfo } from '../models/package-configurator/package-general-info.model';
import { Package, PackageContent, Pricing, buildDefaultPricing } from '../models/package-configurator/package.model';
import { PACKAGES_API_URL } from '../tokens/api.tokens';

/** What changed in a coverage edit session, diffed against its pre-edit baseline — see `PackageConfiguratorStateService.saveDiff`. */
export interface CoverageSaveDiff {
  headerChanged: boolean;
  header: { pricing: Pricing };
  /** New or edited options (each carrying its current, complete sub-option list) — unchanged options are omitted. */
  changedOptions: CoverageOption[];
  /** Ids of options removed since the baseline. */
  deletedOptionIds: string[];
}

/**
 * Access to the flexible packages via the v2 API: the package **core** (`/v2/packages`, metadata)
 * and its effective-dated **coverages** (`/v2/packages/{id}/coverages`, наповнення). Front models match
 * the API; the only shaping done here is normalising optional collections, the category contract
 * (`categoryCode`/`categoryName` ↔ `code`/`nameUa`) and merging a coverage's `activeFrom` from the core.
 */
@Injectable({ providedIn: 'root' })
export class PackageConfiguratorService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(PACKAGES_API_URL);
  private readonly protectedApiUrl = inject(PROTECTED_API_URL);

  /** An empty package-core draft for the create flow. */
  buildNewPackage(): Package {
    return { id: '', ...buildDefaultGeneralInfo() };
  }

  /**
   * Returns the subset of МКХ-10 codes the dictionary does not know. Used to check a parsed coverage
   * workbook before its sheets are imported, so a typo in the file surfaces next to the offending sheet
   * instead of failing the eventual save. An empty result also covers the case where the dictionary is
   * unreachable: the backend treats the codes as known rather than blocking the import.
   */
  findUnknownDiagnosisCodes(codes: string[]): Observable<string[]> {
    const distinct = [...new Set(codes.map(code => code.trim().toUpperCase()).filter(Boolean))];

    if (!distinct.length) return of([]);

    return this.http.post<string[]>(`${this.protectedApiUrl}/dictionaries/icd10/unknown-codes`, distinct).pipe(catchError(() => of([])));
  }

  /** Coverage-categories dictionary (served from the DB) for tagging options / sub-options. */
  getCoverageCategories(): Observable<CoverageCategory[]> {
    return this.http.get<CoverageCategory[]>(`${this.protectedApiUrl}/dictionaries/categories`);
  }

  getPackages(): Observable<Package[]> {
    return this.http.get<Package[]>(`${this.apiUrl}/v2/packages`);
  }

  getPackage(packageId: string): Observable<Package> {
    return this.http.get<Package>(`${this.apiUrl}/v2/packages/${packageId}`);
  }

  createPackage(pkg: Package): Observable<Package> {
    const { name, internalName, description, internalDescription, packageDescriptionUrl } = pkg;

    return this.http.post<Package>(`${this.apiUrl}/v2/packages`, {
      name,
      internalName,
      description,
      internalDescription,
      packageDescriptionUrl,
    });
  }

  updatePackage(pkg: Package): Observable<Package> {
    const { id, name, internalName, description, internalDescription, packageDescriptionUrl, clientType } = pkg;

    return this.http.put<Package>(`${this.apiUrl}/v2/packages/${id}`, {
      id,
      name,
      internalName,
      description,
      internalDescription,
      packageDescriptionUrl,
      clientType,
    });
  }

  /**
   * Every non-cancelled coverage of the package (past, active and scheduled) as a placeholder content built from
   * the core's coverage references — only the id and `activeFrom`, with no pricing or options. The body of each
   * one is fetched on demand with `getCoverage`.
   */
  getCoverageStubs(pkg: Package): PackageContent[] {
    return (pkg.coverages ?? [])
      .filter(reference => !reference.removedAt)
      .map(reference => ({ id: reference.coverageId, activeFrom: reference.activeFrom, pricing: buildDefaultPricing(), options: [] }));
  }

  getCoverage(packageId: string, coverageId: string, activeFrom: string | null): Observable<PackageContent> {
    return this.http.get<CoverageResponse>(`${this.coveragesUrl(packageId)}/${coverageId}`).pipe(map(coverage => fromCoverageResponse(coverage, activeFrom)));
  }

  /** Cancels a scheduled coverage — the backend rejects it once the coverage has started. */
  cancelCoverage(packageId: string, coverageId: string): Observable<void> {
    return this.http.delete<void>(`${this.coveragesUrl(packageId)}/${coverageId}`);
  }

  /** Creates a coverage scheduled from the content's `activeFrom` — the backend generates all ids. */
  createCoverage(packageId: string, content: PackageContent): Observable<PackageContent> {
    return this.http
      .post<CoverageResponse>(this.coveragesUrl(packageId), { ...toContentPayload(content), activeFrom: content.activeFrom })
      .pipe(map(coverage => fromCoverageResponse(coverage, content.activeFrom)));
  }

  /**
   * Updates a persisted coverage. When the active date changed against the persisted one, the coverage is
   * rescheduled first (idempotent for the same coverage). The content update edits the coverage IN PLACE, so
   * its id and its schedule reference are unchanged; options keep their ids and group names.
   *
   * Given a `diff`, only what actually changed is written — the header, each changed option (with its
   * current sub-options) and deletes for removed options — instead of replacing the whole coverage on every
   * save. Without one (no pre-edit baseline to diff against) it falls back to a full content replace.
   */
  updateCoverage(packageId: string, content: PackageContent, persistedActiveFrom: string | null, diff: CoverageSaveDiff | null): Observable<PackageContent> {
    const coverageUrl = `${this.coveragesUrl(packageId)}/${content.id}`;
    const reschedule$ = isSameInstant(content.activeFrom, persistedActiveFrom)
      ? of(null)
      : this.http.put<CoverageResponse>(`${coverageUrl}/schedule`, { activeFrom: content.activeFrom });

    return reschedule$.pipe(switchMap(() => (diff ? this.saveCoverageDiff(packageId, content, diff) : this.replaceCoverageContent(packageId, content))));
  }

  /** Full content replace — the create path, and the update fallback when there is no baseline to diff against. */
  private replaceCoverageContent(packageId: string, content: PackageContent): Observable<PackageContent> {
    return this.http
      .put<CoverageResponse>(`${this.coveragesUrl(packageId)}/${content.id}`, toContentPayload(content))
      .pipe(map(coverage => fromCoverageResponse(coverage, content.activeFrom)));
  }

  /** Sends only the header and the changed/removed options, then folds the responses back into a full `PackageContent`. */
  private saveCoverageDiff(packageId: string, content: PackageContent, diff: CoverageSaveDiff): Observable<PackageContent> {
    const coverageUrl = `${this.coveragesUrl(packageId)}/${content.id}`;
    const optionsUrl = this.optionsUrl(content.id);

    const header$ = diff.headerChanged
      ? this.http.put<{ pricing: PricingResponse | null }>(`${coverageUrl}/header`, { name: UNNAMED_COVERAGE, pricing: toPricingPayload(diff.header.pricing) })
      : of(null);

    const optionWrites$ = diff.changedOptions.length
      ? forkJoin(diff.changedOptions.map(option => this.http.put<CoverageOptionResponse>(optionsUrl, toOptionPayload(option)).pipe(map(saved => ({ localId: option.id, saved })))))
      : of([]);

    const deletes$ = diff.deletedOptionIds.length ? forkJoin(diff.deletedOptionIds.map(id => this.http.delete<void>(`${optionsUrl}/${id}`))) : of([]);

    return forkJoin([header$, optionWrites$, deletes$]).pipe(map(([header, optionResults]) => mergeCoverageSave(content, diff, header, optionResults)));
  }

  private coveragesUrl(packageId: string): string {
    return `${this.apiUrl}/v2/packages/${packageId}/coverages`;
  }

  private optionsUrl(coverageId: string): string {
    return `${this.apiUrl}/v2/coverages/${coverageId}/options`;
  }
}

/** Contents are no longer named, but the API contract still requires a coverage `name`. */
const UNNAMED_COVERAGE = '';

function isSameInstant(left: string | null, right: string | null): boolean {
  if (!left || !right) return left === right;
  return new Date(left).getTime() === new Date(right).getTime();
}

/** Builds the coverage content payload (pricing + options) for a full replace. See `toOptionPayload` for the option shape. */
function toContentPayload(content: PackageContent) {
  return {
    name: UNNAMED_COVERAGE,
    pricing: toPricingPayload(content.pricing),
    options: content.options.map(toOptionPayload),
  };
}

/**
 * Builds one option's payload (id, `groupName` and its full, current sub-option list included) — shared by
 * the whole-content payload and the granular per-option save. Ids and group names are round-tripped, not
 * stripped: an option that keeps its id keeps its document and its audit trail, and one that keeps its
 * `groupName` keeps the identity that prescription snapshots and usage counters join on. Diagnoses are sent
 * as bare codes; the API returns them the same way — names are resolved lazily on the frontend, see
 * `Icd10DictionaryStore`.
 */
function toOptionPayload(option: CoverageOption) {
  return {
    id: option.id,
    groupName: option.groupName,
    name: option.name,
    description: option.description,
    categories: option.categories.map(toCategoryPayload),
    limits: option.limits,
    clinicClasses: option.clinicClasses,
    diagnosisCodes: option.diagnoses.map(diagnosis => diagnosis.code),
    subOptions: option.subOptions.map(sub => ({
      id: sub.id,
      groupName: sub.groupName,
      name: sub.name,
      description: sub.description,
      categories: sub.categories.map(toCategoryPayload),
      limits: sub.limits,
    })),
  };
}

/**
 * Folds a granular save's responses back into a full `PackageContent`: unchanged options pass through as-is,
 * changed/new options are replaced by what the server actually stored (real id, resolved `groupName`), and
 * deleted ones are dropped. Matching is by `option.id` — stable even for a brand-new option, since the
 * client-generated id it was saved under is preserved as its real backend id (not regenerated) on update.
 */
function mergeCoverageSave(
  content: PackageContent,
  diff: CoverageSaveDiff,
  header: { pricing: PricingResponse | null } | null,
  optionResults: { localId: string; saved: CoverageOptionResponse }[]
): PackageContent {
  const savedByLocalId = new Map(optionResults.map(result => [result.localId, fromOptionResponse(result.saved)]));
  const deletedIds = new Set(diff.deletedOptionIds);

  return {
    ...content,
    pricing: header ? fromPricingResponse(header.pricing) : content.pricing,
    options: content.options.filter(option => !deletedIds.has(option.id)).map(option => savedByLocalId.get(option.id) ?? option),
  };
}
